import {
  AlertColor,
  Box,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import React, { FC, useEffect, useState } from 'react';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import Config from '../../../utils/Config';
import axiosInstance from '../../../utils/axios';
import { identifiers } from '../../../utils/constants/identifiers';
import { URLS } from '../../../utils/constants/urls';
import { fetchFromStorage } from '../../../utils/storage';
import { downloadExportFile, getMessage } from '../../../utils/validators/HelperFunctions';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';
import CompanyWiseBobChart from './CompanyWiseBobChart';
import VenueWiseBobChart from './VenueWiseBobChart';
import SegmentWiseBobChart from './SegmentWiseBobChart';
import VenueApcChart from './VenueApcChart';
import SegmentApcChart from './SegmentApcChart';
import TimeApcChart from './TimeApcChart';
import VenueRevenuePerAreaChart from './VenueRevenuePerAreaChart';
import Filter from './Filters';
import SourceOverviewChart from './SourceOverviewChart';
import BudgetOverviewChart from './BudgetOverviewChart';
import {
  AccountBalanceWalletOutlined,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  FileDownloadOutlined,
  InfoOutlined,
  QueryStatsOutlined,
  TrendingUpRounded,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { privileges } from '../../../utils/constants/privileges';
import MiniTrendChart, { MiniTrendPoint } from '../Dashboard/MiniTrendChart';

import ReportStatusCards from './ReportStatusCards';
import OccupancyHeatmap from './OccupancyHeatmap';
import RoomNightsReportTable from './RoomNightsReportTable';
import VenueOccupancyReportTable from './VenueOccupancyReportTable';
import ComplimentaryRevenueByVenueChart from './ComplimentaryRevenueByVenueChart';
import ReportStatusStripe from './ReportStatusStripe';
import {
  reportChartCellFullWidthSx,
  reportChartCellInGridSx,
  reportChartGridItemSx,
  reportChartPaperSx,
  revenueIntelligenceStackSx,
} from './reportChartLayout';
import DimensionPerformanceByMonth from './DimensionPerformanceByMonth';
import DemandCalendar from './DemandCalendar';

dayjs.extend(isSameOrAfter);

interface RevenueSnapshot {
  revenueMonth: string;
  budgetAmount: number | null;
  opening: {
    asOfDate: string;
    pipelineAmount: number;
    expectedAmount: number;
  } | null;
  closing: {
    asOfDate: string;
    actualAmount: number;
  } | null;
  status: string;
  yoyChangePercent?: number | null;
  momChangePercent?: number | null;
  sparklineValues?: number[];
  sparklineLabels?: string[];
}

const REVENUE_SPARKLINE_COLOR = '#1976d2';

const revenuePremiumCardSx = {
  p: { xs: 2, sm: 2.5 },
  borderRadius: '16px',
  backgroundColor: '#FFFFFF',
  border: '1px solid rgba(148, 163, 184, 0.28)',
  boxShadow: '0 18px 45px rgba(15, 23, 42, 0.08)',
};

function buildSparklineData(snapshot: RevenueSnapshot): MiniTrendPoint[] {
  const values = snapshot.sparklineValues ?? [];
  const labels = snapshot.sparklineLabels ?? [];
  return values.map((value, index) => ({
    date: labels[index] ?? '',
    value: Number(value) || 0,
  }));
}

function buildSparklineTooltip(snapshot: RevenueSnapshot, formatAmount: (amount: number) => string): string {
  const points = buildSparklineData(snapshot);
  if (points.length === 0) {
    return '';
  }
  return points.map(point => `${point.date}: ${formatAmount(point.value)}`).join('\n');
}

function hasSparkline(snapshot: RevenueSnapshot): boolean {
  const values = snapshot.sparklineValues ?? [];
  return values.length >= 2 && values.some(v => v > 0);
}

function formatTrendPercent(percent: number): string {
  const rounded = Math.abs(percent);
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function renderSnapshotTrend(percent: number | null | undefined, label: string) {
  if (percent === null || percent === undefined) {
    return null;
  }
  const positive = percent >= 0;
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        display: 'block',
        fontSize: '0.62rem',
        lineHeight: 1.35,
        fontWeight: 600,
        color: positive ? 'success.main' : 'error.main',
        mt: 0.25,
        whiteSpace: 'nowrap',
      }}>
      {positive ? '↑' : '↓'} {formatTrendPercent(percent)}% {label}
    </Typography>
  );
}

function SnapshotStatusChip({ status }: { status: string }) {
  const isClosed = status === 'Closed';
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.6rem',
        fontWeight: 600,
        mt: 0.5,
        px: 0.5,
        py: 0,
        backgroundColor: isClosed ? 'rgba(76, 175, 80, 0.12)' : 'rgba(33, 150, 243, 0.12)',
        color: isClosed ? 'success.main' : 'primary.main',
        border: '1px solid',
        borderColor: isClosed ? 'rgba(76, 175, 80, 0.3)' : 'rgba(33, 150, 243, 0.3)',
        '& .MuiChip-label': {
          padding: '0 4px',
        },
      }}
    />
  );
}

function computeSnapshotMetrics(snapshot: RevenueSnapshot) {
  const actualAmount = snapshot.closing?.actualAmount ?? null;
  const budgetAmount = snapshot.budgetAmount ?? null;
  const actualMet = budgetAmount !== null && actualAmount !== null ? actualAmount >= budgetAmount : null;
  const variance = budgetAmount !== null && actualAmount !== null ? actualAmount - budgetAmount : null;
  const variancePercent =
    budgetAmount !== null && budgetAmount > 0 && variance !== null ? (variance / budgetAmount) * 100 : null;
  const achievementPercent =
    budgetAmount !== null && budgetAmount > 0 && actualAmount !== null ? (actualAmount / budgetAmount) * 100 : null;

  return { actualAmount, actualMet, budgetAmount, variance, variancePercent, achievementPercent };
}

function SnapshotColumnHeader({
  label,
  align = 'right',
  info,
}: {
  label: string;
  align?: 'left' | 'right' | 'center';
  info?: string;
}) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: align === 'center' ? 'center' : align === 'left' ? 'flex-start' : 'flex-end',
        gap: 0.35,
        width: '100%',
      }}>
      <span>{label}</span>
      {info && (
        <Tooltip title={info} arrow placement="top">
          <InfoOutlined sx={{ fontSize: 16, color: 'action.active', cursor: 'help', opacity: 0.75, flexShrink: 0 }} />
        </Tooltip>
      )}
    </Box>
  );
}

const Reports: FC = () => {
  const userPrivileges = useSelector(Privilege);
  const [activeReportTab, setActiveReportTab] = useState<number>(0);
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [filterResetKey, setFilterResetKey] = useState<number>(0);
  const [revenueSnapshots, setRevenueSnapshots] = useState<RevenueSnapshot[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const [dates, setDates] = useState<any>({
    startDate: dayjs().date(1).format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
  });

  const handleExport = (type: string) => {
    const token = fetchFromStorage(identifiers.access_token);
    setLoading(true);
    let filename = 'Revenue_Snapshots';
    axios({
      url: `${Config.BaseURL + URLS.reportsExport(type)}`,
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      data: {
        startDate: dayjs(dates.startDate).format('DD-MM-YYYY'),
        endDate: dayjs(dates.endDate).format('DD-MM-YYYY'),
      },
    })
      .then(response => {
        setLoading(false);
        const match = RegExp(/filename="?(.+?)"?$/).exec(response.headers['content-disposition']);
        if (match) {
          filename = match[1];
        }
        downloadExportFile(response, 'xlsx', filename);
        setMessage({
          display: true,
          severity: 'success',
          message: 'Report exported successfully',
        });
      })
      .catch(err => {
        setLoading(false);
        setMessage({
          display: true,
          severity: 'error',
          message: getMessage(err),
        });
      });
  };

  const handleFilter = async (overrideDates?: { startDate: string; endDate: string }) => {
    try {
      const sendData = {
        startDate: dayjs((overrideDates || dates).startDate).format('DD-MM-YYYY'),
        endDate: dayjs((overrideDates || dates).endDate).format('DD-MM-YYYY'),
      };
      if (dayjs((overrideDates || dates).endDate).isSameOrAfter(dayjs((overrideDates || dates).startDate))) {
        setLoading(true);
        const { data } = await axiosInstance.post(URLS.reports, sendData);
        setLoading(false);
        setPageResponse(data);
      } else {
        setMessage({
          display: true,
          severity: 'warning',
          message: strings.common_endDate_error_message,
        });
      }
    } catch (error: any) {
      console.log(error);
      let errorMessage = 'An error occurred while fetching the report';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = error.response.data?.message || 'No data found for the selected date range';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({
        display: true,
        severity: 'error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const fetchRevenueSnapshots = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(URLS.revenueSnapshots);
      // Sort by revenueMonth in ascending order (oldest to newest: Dec - Jan - Feb)
      const sortedData = [...data].sort((a, b) => {
        // Compare YYYY-MM format strings (works because format is sortable)
        return a.revenueMonth.localeCompare(b.revenueMonth);
      });
      setRevenueSnapshots(sortedData);
    } catch (error: any) {
      console.log(error);
      let errorMessage = 'An error occurred while fetching revenue snapshots';

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = error.response.data?.message || 'No revenue snapshot data found';
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data?.error) {
          errorMessage = error.response.data.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({
        display: true,
        severity: 'error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFilter();
    fetchRevenueSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResetDates = () => {
    const nextDates = {
      startDate: dayjs().date(1).format('YYYY-MM-DD'),
      endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    };
    setDates(nextDates);
    setFilterResetKey(k => k + 1);
    handleFilter(nextDates);
  };

  const handleCLick = (url: string) => {
    if (dayjs(dates.endDate).isSameOrAfter(dayjs(dates.startDate))) {
      const token = fetchFromStorage(identifiers.access_token);
      setLoading(true);
      const exportData = {
        startDate: dayjs(dates.startDate).format('DD-MM-YYYY'),
        endDate: dayjs(dates.endDate).format('DD-MM-YYYY'),
      };

      let filename = 'Export';
      axios({
        url: `${Config.BaseURL + url}`,
        method: 'POST',
        data: exportData,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
        .then(response => {
          setLoading(false);
          const match = RegExp(/filename="?(.+?)"?$/).exec(response.headers['content-disposition']);
          if (match) {
            filename = match[1];
          }
          downloadExportFile(response, 'xlsx', filename);
        })
        .catch(err => {
          console.log(err, 'err', err.response);
          setLoading(false);
          let errorMessage = 'An error occurred while exporting the report';

          if (err.response) {
            // Handle 404 and other error responses
            if (err.response.status === 404) {
              errorMessage = err.response.data?.message || 'No data found for the selected date range';
            } else if (err.response.data?.message) {
              errorMessage = err.response.data.message;
            } else if (err.response.data?.error) {
              errorMessage = err.response.data.error;
            }
          } else if (err.message) {
            errorMessage = err.message;
          }

          setMessage({
            display: true,
            severity: 'error',
            message: errorMessage,
          });
        });
    } else {
      setMessage({
        display: true,
        severity: 'warning',
        message: strings.common_endDate_error_message,
      });
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleDates = (date: { startDate: string; endDate: string }) => {
    setDates(date);
  };

  const formatCurrency = (amount: number) => {
    return formatCurrencyWithSymbol(amount, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatMonth = (monthString: string) => {
    return dayjs(monthString + '-01')
      .format('MMM YY')
      .toUpperCase();
  };

  const revenueSnapshotSummary = revenueSnapshots.reduce(
    (acc, snapshot) => {
      acc.booked += snapshot.opening?.pipelineAmount ?? 0;
      acc.budget += snapshot.budgetAmount ?? 0;
      acc.actual += snapshot.closing?.actualAmount ?? 0;
      return acc;
    },
    { booked: 0, budget: 0, actual: 0 },
  );
  const revenueSnapshotGap = revenueSnapshotSummary.actual - revenueSnapshotSummary.budget;

  const downloadCoverReport = () => {
    const token = fetchFromStorage(identifiers.access_token);
    setLoading(true);

    let filename = 'Cover_Report';
    axios({
      url: `${Config.BaseURL + URLS.exportCoverReport}`,
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    })
      .then(response => {
        setLoading(false);
        const match = RegExp(/filename="?(.+?)"?$/).exec(response.headers['content-disposition'] ?? '');
        if (match) {
          filename = match[1];
        }
        downloadExportFile(response, 'xlsx', filename);
        setMessage({
          display: true,
          severity: 'success',
          message: 'Cover report downloaded successfully',
        });
      })
      .catch(err => {
        setLoading(false);
        setMessage({
          display: true,
          severity: 'error',
          message: getMessage(err),
        });
      });
  };

  const exportRevenueSnapshots = () => {
    const token = fetchFromStorage(identifiers.access_token);
    setLoading(true);

    let filename = 'Revenue_Snapshots';
    axios({
      url: `${Config.BaseURL + URLS.exportRevenueSnapshots}`,
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    })
      .then(response => {
        setLoading(false);
        const match = RegExp(/filename="?(.+?)"?$/).exec(response.headers['content-disposition']);
        if (match) {
          filename = match[1];
        }
        downloadExportFile(response, 'xlsx', filename);
        setMessage({
          display: true,
          severity: 'success',
          message: 'Revenue snapshots exported successfully',
        });
      })
      .catch(err => {
        console.log(err, 'err', err.response);
        setLoading(false);
        let errorMessage = 'An error occurred while exporting revenue snapshots';

        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = err.response.data?.message || 'No revenue snapshot data found';
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.response.data?.error) {
            errorMessage = err.response.data.error;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }

        setMessage({
          display: true,
          severity: 'error',
          message: errorMessage,
        });
      });
  };

  return (
    <Container maxWidth={false} sx={{ backgroundColor: '#F8FAFC', minHeight: '100vh', px: { xs: 0, sm: 3 } }}>
      {loading && <PageLoader />}

      {/* Page header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: { xs: 20, sm: 22 }, fontWeight: 700, lineHeight: 1.2 }}>
            {strings.reports_title_text}
          </Typography>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              minWidth: { xs: 0, sm: 200 },
              maxWidth: { xs: '100%', sm: 'none' },
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'primary.main',
              bgcolor: 'background.paper',
            }}>
            <InfoOutlined sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
            <Typography
              component="span"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1.3,
                color: 'primary.main',
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
              }}>
              {strings.reports_tax_exclusive_disclaimer}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Business performance, revenue analytics and occupancy insights.
        </Typography>
      </Box>

      {/* Filter bar */}
      <Filter
        handleDates={handleDates}
        resetKey={filterResetKey}
        onSearch={overrideDates => handleFilter(overrideDates)}
        onReset={handleResetDates}
        exports={[
          { label: 'Export Summary', onClick: () => handleExport('ALL') },
          ...(userPrivileges?.includes(privileges.add_ang_details)
            ? [{ label: 'ANG Details Report', onClick: () => handleCLick(URLS.exportAngDetailsReport) }]
            : []),
          { label: 'Settlement Report', onClick: () => handleCLick(URLS.exportSettlementReport) },
          { label: 'Cover Report', onClick: downloadCoverReport },
          { label: 'Revenue Snapshots', onClick: exportRevenueSnapshots },
        ]}
      />

      {/* Booking Status Cards */}
      {pageResponse.bookingCounts && (
        <Box sx={{ mb: 3 }}>
          <ReportStatusCards
            data={(pageResponse.bookingCounts || []).filter((x: any) =>
              String(x?.name || '')
                .toUpperCase()
                .includes('_BOB'),
            )}
          />
          <Box sx={{ mt: 1.25 }}>
            <ReportStatusStripe
              data={(pageResponse.bookingCounts || []).filter(
                (x: any) =>
                  !String(x?.name || '')
                    .toUpperCase()
                    .includes('_BOB'),
              )}
            />
          </Box>
        </Box>
      )}

      {/* Report Sub-Tabs */}
      <Box sx={{ mb: 3, mt: 2 }}>
        <Box
          sx={{
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            p: 0.5,
            width: '100%',
            border: '1px solid',
            borderColor: 'divider',
          }}>
          <Tabs
            value={activeReportTab}
            onChange={(_, newValue) => setActiveReportTab(newValue)}
            variant="fullWidth"
            sx={{
              minHeight: 40,
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 40,
                px: 2,
                py: 1.5,
                borderRadius: '6px',
                backgroundColor: 'transparent',
                color: 'text.secondary',
                textAlign: 'center',
                justifyContent: 'center',
                flex: 1,
                fontSize: '12px',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  fontWeight: 600,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                },
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.dark',
                  },
                },
              },
            }}>
            <Tab label="Overview" />
            <Tab label={strings.reports_tab_revenue_intelligence} />
          </Tabs>
        </Box>
      </Box>

      {pageResponse !== undefined || pageResponse !== '' || pageResponse !== null ? (
        <Grid container spacing={2}>
          {message.display && (
            <Notification
              isOpen={message.display}
              message={message.message}
              severity={message.severity as AlertColor}
              closeNotification={closeNotification}
            />
          )}

          {/* TAB 1: Overview */}
          {activeReportTab === 0 && (
            <>
              {/* ═══ SECTION 1: Revenue Performance ═══ */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    color: '#1F2937',
                    mt: 0.5,
                    mb: 0,
                  }}>
                  Revenue Performance
                </Typography>
              </Grid>

              {pageResponse?.monthlyBudgetReport?.datasets?.length > 0 && (
                <Grid item xs={12} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellFullWidthSx}>
                    <BudgetOverviewChart
                      bobData={pageResponse.monthlyBudgetReport}
                      actualData={pageResponse.budgetVersusActualReport}
                      reportDateRange={{ startDate: dates.startDate, endDate: dates.endDate }}
                      handleExport={() => handleExport(pageResponse?.monthlyBudgetReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}
              {pageResponse?.agentWiseRevenueReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <SourceOverviewChart
                      data={pageResponse.agentWiseRevenueReport}
                      handleExport={() => handleExport(pageResponse?.agentWiseRevenueReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}
              {pageResponse?.companyWiseBobReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <CompanyWiseBobChart
                      data={pageResponse.companyWiseBobReport}
                      name={pageResponse.companyWiseBobReport.name}
                      handleExport={() => handleExport(pageResponse?.companyWiseBobReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}

              {/* ═══ SECTION 2: Business Mix & Utilization ═══ */}
              <Grid item xs={12}>
                <Box sx={{ borderTop: '1px solid #E5E7EB', mt: 1.5, pt: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      letterSpacing: '0.2px',
                      color: '#1F2937',
                      mb: 0,
                    }}>
                    Business Mix & Utilization
                  </Typography>
                </Box>
              </Grid>

              {pageResponse?.venueWiseBobReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <VenueWiseBobChart
                      data={pageResponse.venueWiseBobReport}
                      name={pageResponse.venueWiseBobReport.name}
                      handleExport={() => handleExport(pageResponse?.venueWiseBobReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}
              {pageResponse?.eventTypeWiseBobReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <SegmentWiseBobChart
                      data={pageResponse.eventTypeWiseBobReport}
                      name={pageResponse.eventTypeWiseBobReport.name}
                      handleExport={() => handleExport(pageResponse?.eventTypeWiseBobReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}

              {/* {pageResponse?.timingWiseVenueOccupancyPercentage?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <OccupancyHeatmap
                      data={pageResponse.timingWiseVenueOccupancyPercentage}
                      name={pageResponse.timingWiseVenueOccupancyPercentage.name}
                      handleExport={() => handleExport(pageResponse?.timingWiseVenueOccupancyPercentage.reportType)}
                    />
                  </Box>
                </Grid>
              )} */}

              {(pageResponse?.roomNightsReport?.rows?.length ?? 0) > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <RoomNightsReportTable
                      data={pageResponse.roomNightsReport}
                      name={pageResponse.roomNightsReport.name}
                      handleExport={() => handleExport(pageResponse?.roomNightsReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}

              {(pageResponse?.complementaryRevenueByVenueReport?.labels?.length ?? 0) > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <ComplimentaryRevenueByVenueChart
                      data={pageResponse.complementaryRevenueByVenueReport}
                      name={pageResponse.complementaryRevenueByVenueReport.name}
                      handleExport={() => handleExport(pageResponse?.complementaryRevenueByVenueReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}

              {/* ═══ SECTION 3: Yield & Efficiency ═══ */}
              <Grid item xs={12}>
                <Box sx={{ borderTop: '1px solid #E5E7EB', mt: 1.5, pt: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 700,
                      letterSpacing: '0.2px',
                      color: '#1F2937',
                      mb: 0,
                    }}>
                    Yield & Efficiency
                  </Typography>
                </Box>
              </Grid>

              {pageResponse?.venueAveragePerCustomerWiseBobReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <VenueApcChart
                      data={pageResponse.venueAveragePerCustomerWiseBobReport}
                      name={pageResponse.venueAveragePerCustomerWiseBobReport.name}
                      handleExport={() => handleExport(pageResponse?.venueAveragePerCustomerWiseBobReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}
              {pageResponse?.eventTypeAveragePerCustomerWiseBobReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <SegmentApcChart
                      data={pageResponse.eventTypeAveragePerCustomerWiseBobReport}
                      name={pageResponse.eventTypeAveragePerCustomerWiseBobReport.name}
                      handleExport={() =>
                        handleExport(pageResponse?.eventTypeAveragePerCustomerWiseBobReport.reportType)
                      }
                    />
                  </Box>
                </Grid>
              )}

              {pageResponse?.timingsAveragePerCustomerWiseBobReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <TimeApcChart
                      data={pageResponse.timingsAveragePerCustomerWiseBobReport}
                      name={pageResponse.timingsAveragePerCustomerWiseBobReport.name}
                      handleExport={() => handleExport(pageResponse?.timingsAveragePerCustomerWiseBobReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}
              {pageResponse?.venueWiseRevenuePerAreaReport?.datasets?.length > 0 && (
                <Grid item xs={12} md={6} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellInGridSx}>
                    <VenueRevenuePerAreaChart
                      data={pageResponse?.venueWiseRevenuePerAreaReport}
                      name={pageResponse?.venueWiseRevenuePerAreaReport.name}
                      handleExport={() => handleExport(pageResponse?.venueWiseRevenuePerAreaReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}

              {(pageResponse?.venueOccupancyReport?.rows?.length ?? 0) > 0 && (
                <Grid item xs={12} sx={reportChartGridItemSx}>
                  <Box sx={reportChartCellFullWidthSx}>
                    <VenueOccupancyReportTable
                      data={pageResponse.venueOccupancyReport}
                      name={pageResponse.venueOccupancyReport.name}
                      handleExport={() => handleExport(pageResponse?.venueOccupancyReport.reportType)}
                    />
                  </Box>
                </Grid>
              )}
            </>
          )}

          {/* TAB 2: Revenue Intelligence */}
          {activeReportTab === 1 && (
            <Grid item xs={12}>
              <Box sx={revenueIntelligenceStackSx}>
                {revenueSnapshots.length > 0 && (
                  <Paper sx={{ ...reportChartPaperSx, ...revenuePremiumCardSx }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: 2,
                      }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A' }}>
                          Revenue Snapshot
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B', mt: 0.4 }}>
                          Booked revenue, budget and actualized performance by month.
                        </Typography>
                      </Box>
                      <Tooltip title="Export Revenue Snapshots" arrow>
                        <IconButton
                          onClick={exportRevenueSnapshots}
                          sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            borderRadius: '8px',
                            color: 'primary.main',
                            p: 0.9,
                            flexShrink: 0,
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                          }}>
                          <FileDownloadOutlined />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      {[
                        {
                          label: 'Booked',
                          value: formatCurrency(revenueSnapshotSummary.booked),
                          icon: <AccountBalanceWalletOutlined />,
                          color: '#1565C0',
                          bg: 'rgba(25, 118, 210, 0.1)',
                        },
                        {
                          label: 'Budget',
                          value: formatCurrency(revenueSnapshotSummary.budget),
                          icon: <QueryStatsOutlined />,
                          color: '#0F766E',
                          bg: 'rgba(20, 184, 166, 0.12)',
                        },
                        {
                          label: 'Actual',
                          value: formatCurrency(revenueSnapshotSummary.actual),
                          icon: <TrendingUpRounded />,
                          color: '#2E7D32',
                          bg: 'rgba(76, 175, 80, 0.12)',
                        },
                        {
                          label: 'Gap',
                          value: formatCurrency(revenueSnapshotGap),
                          icon: revenueSnapshotGap >= 0 ? <ArrowUpwardRounded /> : <ArrowDownwardRounded />,
                          color: revenueSnapshotGap >= 0 ? '#2E7D32' : '#C62828',
                          bg: revenueSnapshotGap >= 0 ? 'rgba(76, 175, 80, 0.12)' : 'rgba(244, 67, 54, 0.1)',
                        },
                      ].map(item => (
                        <Grid item xs={12} sm={6} md={3} key={item.label}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.25,
                              height: '100%',
                              px: 1.5,
                              py: 1.25,
                              borderRadius: '14px',
                              border: '1px solid rgba(148, 163, 184, 0.22)',
                              background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
                            }}>
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '12px',
                                display: 'grid',
                                placeItems: 'center',
                                bgcolor: item.bg,
                                color: item.color,
                                flexShrink: 0,
                                '& svg': { fontSize: 20 },
                              }}>
                              {item.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
                                {item.label}
                              </Typography>
                              <Typography
                                variant="subtitle1"
                                sx={{ color: item.color, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                                {item.value}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <TableContainer
                      sx={{
                        border: '1px solid',
                        borderColor: 'rgba(148, 163, 184, 0.28)',
                        borderRadius: '14px',
                        overflow: 'auto',
                      }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800, minWidth: 108, bgcolor: '#F8FAFC' }}>
                              {strings.budget_text_month}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              BoB
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              <SnapshotColumnHeader label="Budget" />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              <SnapshotColumnHeader label="Actual" />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              <SnapshotColumnHeader
                                label="Variance"
                                info={strings.reports_revenue_snapshot_variance_info}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                              <SnapshotColumnHeader
                                label={strings.reports_revenue_snapshot_achievement_pct}
                                info={strings.reports_revenue_snapshot_achievement_info}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 100 }}>
                              <SnapshotColumnHeader
                                label="Trend"
                                info="Daily revenue trend for the month"
                                align="center"
                              />
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {revenueSnapshots.map((snapshot, index) => {
                            const {
                              actualAmount,
                              actualMet,
                              budgetAmount,
                              variance,
                              variancePercent,
                              achievementPercent,
                            } = computeSnapshotMetrics(snapshot);

                            return (
                              <TableRow key={index} hover>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.primary' }}>
                                    {formatMonth(snapshot.revenueMonth)}
                                  </Typography>
                                  <SnapshotStatusChip status={snapshot.status} />
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 500, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}>
                                  {snapshot.opening ? formatCurrency(snapshot.opening.pipelineAmount) : '—'}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ fontWeight: 500, color: 'primary.dark', fontVariantNumeric: 'tabular-nums' }}>
                                  {budgetAmount !== null ? formatCurrency(budgetAmount) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-end',
                                    }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        fontVariantNumeric: 'tabular-nums',
                                        whiteSpace: 'nowrap',
                                        color:
                                          actualMet === true
                                            ? 'success.main'
                                            : actualMet === false
                                            ? 'error.main'
                                            : 'text.primary',
                                      }}>
                                      {actualAmount ? formatCurrency(actualAmount) : '—'}
                                    </Typography>
                                    {renderSnapshotTrend(
                                      snapshot.yoyChangePercent,
                                      strings.reports_revenue_snapshot_vs_last_year,
                                    )}
                                    {renderSnapshotTrend(
                                      snapshot.momChangePercent,
                                      strings.reports_revenue_snapshot_vs_previous_month,
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                                  {variance !== null ? (
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                      }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '0.875rem',
                                          fontVariantNumeric: 'tabular-nums',
                                          color:
                                            variance >= 0
                                              ? 'success.main'
                                              : variance < 0
                                              ? 'error.main'
                                              : 'text.secondary',
                                        }}>
                                        {variance >= 0 ? '+' : ''}
                                        {formatCurrency(variance)}
                                      </Typography>
                                      {variancePercent !== null && (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            fontSize: '0.68rem',
                                            fontWeight: 600,
                                            lineHeight: 1.35,
                                            fontVariantNumeric: 'tabular-nums',
                                            color:
                                              variancePercent >= 0
                                                ? 'success.main'
                                                : variancePercent < 0
                                                ? 'error.main'
                                                : 'text.secondary',
                                          }}>
                                          {variancePercent >= 0 ? '+' : ''}
                                          {variancePercent.toFixed(1)}%
                                        </Typography>
                                      )}
                                    </Box>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    fontVariantNumeric: 'tabular-nums',
                                    color:
                                      achievementPercent !== null
                                        ? achievementPercent >= 100
                                          ? 'success.main'
                                          : 'error.main'
                                        : 'text.secondary',
                                  }}>
                                  {achievementPercent !== null ? `${achievementPercent.toFixed(1)}%` : '—'}
                                </TableCell>
                                <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                                  {hasSparkline(snapshot) ? (
                                    <Tooltip
                                      title={
                                        <Box component="span" sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>
                                          {buildSparklineTooltip(snapshot, formatCurrency)}
                                        </Box>
                                      }
                                      arrow
                                      placement="top">
                                      <Box sx={{ width: 92, height: 40, cursor: 'default', mx: 'auto' }}>
                                        <MiniTrendChart
                                          data={buildSparklineData(snapshot)}
                                          color={REVENUE_SPARKLINE_COLOR}
                                          variant="sparkline"
                                        />
                                      </Box>
                                    </Tooltip>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                <DimensionPerformanceByMonth startDate={dates.startDate} endDate={dates.endDate} />

                <DemandCalendar
                  onExportMessage={(severity, message) => setMessage({ display: true, severity, message })}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      ) : (
        <h3>{strings.no_data_available}</h3>
      )}
    </Container>
  );
};
export default Reports;
